import { useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { skipToken } from '@reduxjs/toolkit/query'
import withStudentAuth from 'common/HOC/withStudentAuth'
import { useAppDispatch, useAppSelector } from 'app/hooks'
import { logout, selectUser } from 'app/reducer/authSlice'
import {
  useDismissStudentNotificationMutation,
  useGetStudentClassroomAssessmentsQuery,
  useGetStudentClassroomsQuery
} from 'services/studentApi'
import cookie from 'js-cookie'
import { useNavigate } from 'react-router-dom'
import AlphaPlusLogo from 'assets/img/logo.svg'
import ClassroomHeaderBar from 'components/ClassroomHeaderBar/ClassroomHeaderBar'
import ClassRooms from 'services/modals/ClassRooms'
import FormativeObjectivesAccordion from 'components/ClassroomObjectivesAccordion/FormativeObjectivesAccordion'
import SummativeObjectivesAccordion from 'components/ClassroomObjectivesAccordion/SummativeObjectivesAccordion'
import {
  ContentArea,
  ContentFrame,
  GreetingRow,
  GreetingText,
  HeaderTopRow,
  LayoutRoot,
  NoAssignmentsContainer,
  NoAssignmentsHeading,
  NoAssignmentsText,
  Section,
  SectionHeading,
  SignOutText,
  TopHeader
} from './styles'
import ActivityFeedCarousel from './ActivityFeedCarousel'
import { FlexBox } from 'common/common-styles'
import { selectCurrentClassroom } from 'app/reducer/studentSlice'
import { setSelectedAssessment } from 'app/reducer/assessmentSlice'
import socketKeys from 'components/Assessment/socket-keys'
import { QuestionnaireContext } from 'components/Assessment/QuestionnaireContext'
import { isAllowedNotification } from 'components/ClassroomObjectivesAccordion/assessmentService'
import { ViewMode } from 'interfaces/app'
import { StudentNotificationType } from 'types/assessments'
import { getStudentInitials, getStudentName } from 'util/student'

type FormativeFocusRow =
  | 'formative_quiz'
  | 'lesson_guided_practice'
  | 'independent_practice'
  | 'continuous_practice'

type FocusTarget =
  | {
      section: 'formative'
      formativeId: number
      row: FormativeFocusRow
    }
  | {
      section: 'benchmark'
      summativeId: number
    }

const ClassroomDashboard = () => {
  const user = useAppSelector(selectUser)
  const currentClassroom = useAppSelector(selectCurrentClassroom)

  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const { socketRef, isSocketReady } = useContext(QuestionnaireContext)

  const studentName = getStudentName(user)
  const studentInitials = getStudentInitials(user)

  const [selectedClassroomId, setSelectedClassroomId] = useState<number | null>(
    null
  )
  const [focusTarget, setFocusTarget] = useState<FocusTarget | null>(null)
  const [dismissedCardIds, setDismissedCardIds] = useState<
    Set<number | string>
  >(new Set())

  const { data: classroomsData, isLoading: isClassroomsLoading } =
    useGetStudentClassroomsQuery(undefined, {
      skip: !user,
      refetchOnMountOrArgChange: true
    })

  const classrooms = (classroomsData?.classrooms || []) as ClassRooms

  const {
    data: classroomAssessments,
    isLoading: isAssessmentsLoading,
    refetch: refetchAssessments,
    isFetching: isAssessmentsFetching
  } = useGetStudentClassroomAssessmentsQuery(selectedClassroomId ?? skipToken, {
    skip: !user,
    refetchOnMountOrArgChange: true
  })

  const handleAssessmentNotification = useCallback(
    (data: any) => {
      if (data?.resource) {
        console.log('Received classroom update via socket', data.resource)
        if (isAllowedNotification(data.resource?.type)) {
          refetchAssessments()
        }
      }
    },
    [refetchAssessments]
  )

  useEffect(() => {
    // we do not want to subscribe to socket events if there is no classroom selected, as the events are classroom-specific
    const socket = socketRef.current

    if (!isSocketReady || !socket || !selectedClassroomId) return

    socket.emit(
      socketKeys.SOCKET_STUDENT_DASHBOARD_EVENTS,
      {
        classroom_id: selectedClassroomId
      },
      (subscribed: any) => {
        if (subscribed) {
          socket.on(
            socketKeys.SOCKET_CLASS_BY_ID_CHANNEL,
            handleAssessmentNotification
          )
        }
      }
    )

    return () => {
      socket.off(
        socketKeys.SOCKET_CLASS_BY_ID_CHANNEL,
        handleAssessmentNotification
      )
    }
  }, [isSocketReady, socketRef, selectedClassroomId, handleAssessmentNotification])

  const [dismissStudentNotification, dismissNotificationState] =
    useDismissStudentNotificationMutation()
  const isDismissingNotification = dismissNotificationState.isLoading
  const dismissingNotificationId = dismissNotificationState.originalArgs
  const cards = useMemo(
    () =>
      (classroomAssessments?.activity_feed || []).map((item, idx) => {
        return {
          id: item.id,
          type: item.type,
          title: item.title,
          label: item.label,
          icon: item.icon,
          entityType: item.entity_type,
          formativeId: item.formative_id,
          summativeId: item.summative_id,
          assignmentId: item.assignment_id,
          createdAt: item.created_at
        }
      }),
    [classroomAssessments?.activity_feed]
  )

  const activityCards = useMemo(() => {
    return cards.filter((card) => !dismissedCardIds.has(card.id))
  }, [cards, dismissedCardIds])

  const isAssessmentsPending = isAssessmentsLoading || isAssessmentsFetching

  useEffect(() => {
    setDismissedCardIds(new Set())
    setFocusTarget(null)
  }, [selectedClassroomId])

  const handleActivityCardClick = (card: {
    id: number
    type: string
    formativeId?: number
    summativeId?: number
  }) => {
    const id = card.formativeId || card.summativeId || card.id

    /**
     * Clicking an activity feed item should keep the existing focus behavior
     * and also dismiss the same notification through the existing API path.
     */
    if (card.type === StudentNotificationType.SUMMATIVE_ASSIGNED && id) {
      setFocusTarget({ section: 'benchmark', summativeId: id })
      handleDismissCard(card.id)
      return
    }

    if (card.type === StudentNotificationType.LESSON_GUIDED_ASSIGNED && id) {
      setFocusTarget({
        section: 'formative',
        formativeId: id,
        row: 'lesson_guided_practice'
      })
      handleDismissCard(card.id)
      return
    }

    if (card.type === StudentNotificationType.INDEPENDENT_ASSIGNED && id) {
      setFocusTarget({
        section: 'formative',
        formativeId: id,
        row: 'independent_practice'
      })
      handleDismissCard(card.id)
      return
    }

    if (card.type === StudentNotificationType.CONTINUOUS_ASSIGNED && id) {
      setFocusTarget({
        section: 'formative',
        formativeId: id,
        row: 'continuous_practice'
      })
      handleDismissCard(card.id)
      return
    }

    if (card.type === StudentNotificationType.ASSESSMENTS_GRADED && id) {
      setFocusTarget({
        section: 'formative',
        formativeId: id,
        row: 'formative_quiz'
      })
      handleDismissCard(card.id)
      return
    }

    if (card.type === StudentNotificationType.FORMATIVE_ASSIGNED && id) {
      setFocusTarget({
        section: 'formative',
        formativeId: id,
        row: 'formative_quiz'
      })
      handleDismissCard(card.id)
      return
    }

    if (id) {
      setFocusTarget({
        section: 'formative',
        formativeId: id,
        row: 'formative_quiz'
      })
      handleDismissCard(card.id)
      return
    }

    if (card.summativeId) {
      setFocusTarget({ section: 'benchmark', summativeId: card.summativeId })
      handleDismissCard(card.id)
    }
  }

  const handleDismissCard = async (cardId: number) => {
    if (dismissedCardIds.has(cardId) || isDismissingNotification) return

    try {
      await dismissStudentNotification(cardId).unwrap()
      setDismissedCardIds((previousIds) => {
        const nextIds = new Set(previousIds)
        nextIds.add(cardId)
        return nextIds
      })
    } catch (error) {
      // We intentionally keep the card visible if the API dismissal fails.
      console.error('Failed to dismiss notification', error)
    }
  }

  const onSignOut = async () => {
    await dispatch(logout())
    navigate('/')
  }

  const onStartOrResume = (type: any, row: any) => {
    const selectedClass = currentClassroom

    const assessmentName = row?.name

    if (!selectedClass) return
    if (!assessmentName) return

    const isMath =
      selectedClass?.fk_subject_id === 1 ||
      selectedClass?.subject_id === 1 ||
      selectedClass?.name?.toLowerCase()?.includes('math') ||
      false
    cookie.set('isMath', `${isMath}`)
    cookie.set('classroom_id', `${selectedClass?.id}`)
    cookie.set('class_name', selectedClass.name)
    cookie.set(
      'text_to_speech_enabled',
      `${selectedClass.text_to_speech_enabled}`
    )
    cookie.set('assessmentName', assessmentName)
    dispatch(setSelectedAssessment(assessmentName))

    /**
     * Assignment launches should join the monitoring room before navigation so
     * teacher-side socket events can target the student immediately on open.
     */
    if (type === 'assignments') {
      const studentId = cookie.get('student_id')
      const socket = socketRef.current

      if (socket && selectedClass?.id && studentId && row?.id) {
        socket.emit(socketKeys.SOCKET_STUDENT_JOIN_ROOM, {
          classroom_id: `${selectedClass.id}`,
          student_id: studentId,
          test_type: 'assignment',
          test_id: row.id
        })
      }
    }

    /**
     * Completed Benchmark A2 launches in read only mode so the submitted
     * answers can render alongside the benchmark correctness state.
     */
    const isReadOnlyBenchmarkView =
      type === 'benchmarks' &&
      row?.actionLabel === 'View' &&
      row?.name?.toLowerCase()?.includes('a2')

    const nextUrl = isReadOnlyBenchmarkView
      ? `/${type}/${row.id}?viewMode=${ViewMode.Student}`
      : `/${type}/${row.id}`

    return navigate(nextUrl)
  }

  const summativesLoading =
    isAssessmentsPending || Number(classroomAssessments?.summatives?.length) > 0
  const formativesLoading =
    isAssessmentsPending || Number(classroomAssessments?.formatives?.length) > 0
  const completedFormativesLoading =
    isAssessmentsPending ||
    Number(classroomAssessments?.completed_formatives?.length) > 0
  /**
   * The classroom empty state should only show when both active and completed
   * formative objective groups are empty, because completed cards remain part
   * of the student's study flow even after the active section clears out.
   */
  const hasFormativeObjectives =
    Boolean(classroomAssessments?.formatives?.length) ||
    Boolean(classroomAssessments?.completed_formatives?.length)

  return (
    <LayoutRoot>
      <TopHeader>
        <img
          src={AlphaPlusLogo}
          alt='Alpha Plus Logo'
          width={70}
          style={{ position: 'absolute', left: 100, top: 15 }}
        />
        <HeaderTopRow>
          <ClassroomHeaderBar
            classrooms={classrooms}
            isLoading={isClassroomsLoading}
            autoOpenSelectOnLoad
            studentInitials={studentInitials}
            onClassroomChange={setSelectedClassroomId}
          />
        </HeaderTopRow>
        <GreetingRow>
          <GreetingText>
            Hello, {studentName.firstName}!
            <SignOutText onClick={onSignOut}>Sign Out</SignOutText>
          </GreetingText>
        </GreetingRow>
      </TopHeader>

      <ActivityFeedCarousel
        activityCards={activityCards}
        isAssessmentsPending={isAssessmentsPending}
        isReloadDisabled={!selectedClassroomId}
        isDismissingNotification={isDismissingNotification}
        dismissingNotificationId={dismissingNotificationId}
        onReload={refetchAssessments}
        onDismissCard={handleDismissCard}
        onCardClick={handleActivityCardClick}
      />

      <ContentArea>
        <ContentFrame>
          {selectedClassroomId && (
            <>
              <div className='flex-col'>
                {summativesLoading && (
                  <Section style={{ marginTop: 15 }}>
                    <SectionHeading>Benchmarks</SectionHeading>
                    <SummativeObjectivesAccordion
                      onStartOrResume={onStartOrResume}
                      summatives={classroomAssessments?.summatives || []}
                      loading={isAssessmentsPending}
                      focusedSummativeId={
                        focusTarget?.section === 'benchmark'
                          ? focusTarget.summativeId
                          : null
                      }
                      onFocusHandled={() => setFocusTarget(null)}
                    />
                  </Section>
                )}
              </div>
            </>
          )}
          <div>
            {formativesLoading && (
              <Section>
                <SectionHeading>
                  {classroomAssessments?.lesson_practice_enabled
                    ? 'Lessons and Formative Assessments'
                    : 'Formative Assessments'}
                </SectionHeading>

                <FormativeObjectivesAccordion
                  onStartOrResume={onStartOrResume}
                  formatives={classroomAssessments?.formatives || []}
                  loading={isAssessmentsPending}
                  focusedFormativeId={
                    focusTarget?.section === 'formative'
                      ? focusTarget.formativeId
                      : null
                  }
                  focusedRowType={
                    focusTarget?.section === 'formative'
                      ? focusTarget.row
                      : null
                  }
                  onFocusHandled={() => setFocusTarget(null)}
                />
              </Section>
            )}

            {completedFormativesLoading && (
              <Section>
                <SectionHeading>
                  {classroomAssessments?.lesson_practice_enabled
                    ? 'Completed Lessons and Formative Assessments'
                    : 'Completed Formative Assessments'}
                </SectionHeading>

                <FormativeObjectivesAccordion
                  onStartOrResume={onStartOrResume}
                  formatives={classroomAssessments?.completed_formatives || []}
                  loading={isAssessmentsPending}
                />
              </Section>
            )}

            {!isAssessmentsPending && !hasFormativeObjectives && (
              <FlexBox>
                <NoAssignmentsContainer>
                  <NoAssignmentsHeading>
                    No Assignments Yet
                  </NoAssignmentsHeading>
                  <NoAssignmentsText>
                    Your teacher has not assigned you any work for this class.
                    When work is available, it will appear here.
                  </NoAssignmentsText>
                </NoAssignmentsContainer>
              </FlexBox>
            )}
          </div>
        </ContentFrame>
      </ContentArea>
    </LayoutRoot>
  )
}

export default withStudentAuth(ClassroomDashboard)
